import { forwardRef, useMemo } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ro } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('ro', ro);

const pad = (n) => String(n).padStart(2, '0');

// Convertește 'YYYY-MM-DD' într-un obiect Date local (NU UTC — altfel poate sări o zi
// din cauza fusului orar). Returnează null pentru valori goale/invalide.
const parseDate = (v) => {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  const [y, m, d] = v.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

// Convertește un obiect Date în 'YYYY-MM-DD' folosind ora locală (nu toISOString —
// aia e UTC și poate da off-by-one în fuse orare cu offset negativ).
const formatYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Custom trigger button — arată ca un input din aplicație: chenar rotunjit, iconiță calendar,
// focus ring. Click pe el deschide popup-ul calendar.
const TriggerInput = forwardRef(({ value, onClick, disabled, hasError, placeholder }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    disabled={disabled}
    className={`w-full flex items-center gap-2 px-3 py-2 text-left rounded-lg border bg-white dark:bg-slate-800 transition ${
      hasError
        ? 'border-red-400 dark:border-red-700'
        : 'border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} focus:outline-none`}
  >
    <svg
      className="w-5 h-5 text-slate-400 dark:text-slate-400 flex-shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
      />
    </svg>
    <span
      className={`flex-1 text-sm ${value ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400 dark:text-slate-400'}`}
    >
      {value || placeholder || 'Alege data'}
    </span>
    <svg
      className="w-4 h-4 text-slate-400 dark:text-slate-400 flex-shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </button>
));
TriggerInput.displayName = 'DatePickerTrigger';

// Selector unitar de dată — calendar modern (react-datepicker) cu popup, locale RO,
// dropdown direct la an (rapid pentru date de naștere). Valoarea către părinte rămâne
// string 'YYYY-MM-DD' (gol = ''). API: value, onChange, disabled, yearStart, error.
// `maxDate` override: undefined = azi (default, retro-compatibil), null = fără limită,
// Date = limită exactă. `birthDate=true` aplică restricția logică pentru data nașterii
// (max = today - `minAgeYears`; default 10 ani vârstă minimă) + validare internă cu chenar roșu.
const DateSelect = ({
  value,
  onChange,
  disabled = false,
  yearStart = 1940,
  error,
  placeholder,
  maxDate,
  birthDate = false,
  minAgeYears = 10,
}) => {
  const selected = useMemo(() => parseDate(value), [value]);
  const today = useMemo(() => new Date(), []);
  const minDate = useMemo(() => new Date(yearStart, 0, 1), [yearStart]);
  // Calculează maxDate efectiv: prop explicit > birthDate (today - minAgeYears) > default (azi).
  // Pentru data nașterii, calendarul greys out anii recenți (sub vârsta minimă) plus viitorul.
  const effectiveMaxDate = useMemo(() => {
    if (maxDate === null) return undefined; // null = fără limită
    if (maxDate instanceof Date) return maxDate;
    if (birthDate) {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() - minAgeYears);
      return d;
    }
    return today;
  }, [maxDate, birthDate, minAgeYears, today]);

  // Validare internă birth date: chenar roșu + mesaj LA SELECTARE, nu doar la salvare.
  // Catches cazuri în care valoarea curentă cade în viitor sau în ultimele minAgeYears
  // (ex: date încărcate din DB cu valori istorice greșite, paste, edit manual).
  const internalBirthDateError = useMemo(() => {
    if (!birthDate || !selected) return null;
    if (selected > today) return 'Data nașterii nu poate fi în viitor.';
    const ageMs = today.getTime() - selected.getTime();
    const ageYears = ageMs / (365.25 * 24 * 60 * 60 * 1000);
    if (ageYears < minAgeYears) return `Vârsta minimă acceptată: ${minAgeYears} ani.`;
    return null;
  }, [birthDate, selected, today, minAgeYears]);

  const hasError = !!error || !!internalBirthDateError;
  const displayError = error || internalBirthDateError;

  return (
    <div>
      <DatePicker
        selected={selected}
        onChange={(date) => onChange(date ? formatYMD(date) : '')}
        locale="ro"
        dateFormat="d MMMM yyyy"
        minDate={minDate}
        maxDate={effectiveMaxDate}
        disabled={disabled}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        yearDropdownItemNumber={today.getFullYear() - yearStart + 1}
        scrollableYearDropdown
        popperPlacement="bottom-start"
        // Schimbă luna când schimbi anul din dropdown — UX standard.
        adjustDateOnChange
        customInput={
          <TriggerInput hasError={hasError} disabled={disabled} placeholder={placeholder} />
        }
      />
      {displayError && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">⚠ {displayError}</p>
      )}
    </div>
  );
};

export default DateSelect;
